'use client';

import { FC, ForwardedRef, forwardRef, useRef } from 'react';

import { useIsVisible } from 'hooks/useIsVisible';
import { ShowMoreText } from './ShowMoreText';

interface IBenefits {
  title: string;
  description: string;
  benefits: string[];
  isPreview?: boolean;
}

const Benefit: FC<{ index: number, item: string }> = (props) => {
  const itemRef = useRef(null);

  const isVisibleItem = useIsVisible(itemRef);

  return (
    <div
      ref={itemRef}
      className={`border border-[#e6e6f1] p-5 rounded-lg hover:shadow-[6px_5px_41px_6px_rgba(1,1,18,.08)] duration-1000 transition-all ${isVisibleItem ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-full'}`}
    >
      <div className="flex items-center gap-2.5">
        <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#eff0ff]">
          <div
            className="w-6 h-6 rounded-full bg-purple-blue flex items-center justify-center text-white font-poppins text-sm">{props.index + 1}</div>
        </div>
        <p className="font-poppins text-dark-blue text-sm">{props.item}</p>
      </div>
    </div>
  );
};

export const Benefits = forwardRef((props: IBenefits, ref: ForwardedRef<HTMLDivElement>) => {
  const { benefits, description, title, isPreview } = props;

  const titleRef = useRef(null);
  const descriptionRef = useRef(null);

  const isVisibleTitle = useIsVisible(titleRef);
  const isVisibleDescription = useIsVisible(descriptionRef);

  return (
    <div className="flex items-center justify-center p-10 gap-3.5 sm-1/2:p-5" ref={ref}>
      <div className="container">
        <div className="py-9 flex flex-col items-center justify-center gap-3.5 md:px-0">
          <h2
            ref={titleRef}
            className={`text-dark-blue font-bold font-poppins text-4xl duration-1000 transition-all ${isVisibleTitle ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full'}`}
          >
            {title}
          </h2>
          <div
            ref={descriptionRef}
            className={`w-3/5 text-[#8895aa] font-poppins text-lg md:w-full ${isVisibleDescription ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full'}`}
          >
            {isPreview ? description : <ShowMoreText text={description} />}
          </div>
          <div className="grid grid-cols-2 w-full gap-6 mt-5 md:grid-cols-1">
            {benefits.map((item, index) => (
              <Benefit
                item={item}
                key={index}
                index={index}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
});

Benefits.displayName = 'Benefits';
